import { observer } from 'mobx-react-lite';
import React, { FC, useMemo } from 'react';

import { DeviceDetails } from '@gem/components';
import {
  IcBlock24Icon,
  IcUnlockIcon,
  IcPending16Icon,
  IcBlock16Icon,
  IcStopPoll24Icon,
  IcShield16Icon,
} from '@gem/icons';
import { useTranslation } from '@gem/modules/i18n';
import { ICommonPopupsProps, Popup, useClosePopupOnNavigation } from '@gem/popups';
import { Divider, IconView, IconViewSizes, MenuItem, MenuItemStyle } from '@gem/ui-kit';

import { DeviceStatus } from 'APP/model/userSettings/userSettingsModel.types';

import {
  ISessionsItemDetailsPopupParams,
  useSessionsItemDetailsPopupPresenter,
} from './SessionsItemDetailsPopup.presenter';
import styles from './SessionsItemDetailsPopup.styles.m.css';

export const SessionsItemDetailsPopup: FC<ICommonPopupsProps<ISessionsItemDetailsPopupParams>> =
  observer((props) => {
    const { t } = useTranslation();
    const presenter = useSessionsItemDetailsPopupPresenter(props);

    useClosePopupOnNavigation(props.popupInstance);

    const badgeIcon = useMemo(() => {
      if (presenter.session?.device.status === DeviceStatus.Pending) {
        return IcPending16Icon;
      }

      if (presenter.session?.device.status === DeviceStatus.Blocked) {
        return IcBlock16Icon;
      }

      return null;
    }, [presenter.session?.device.status]);

    if (!presenter.session || !presenter.device) {
      return null;
    }

    return (
      <Popup
        isOpened
        handlePrevStep={presenter.hasBackButton ? presenter.onBack : null}
        onClose={presenter.onClose}
      >
        <DeviceDetails
          device={presenter.device}
          projectName={PROJECT_CONFIG.projectName}
          deviceSession={presenter.deviceSession}
          badgeIcon={badgeIcon}
        />
        <Divider theme="style7" />
        {presenter.session.device.status === DeviceStatus.Trusted && (
          <>
            <MenuItem
              size="medium-2"
              style={MenuItemStyle.BrandPrimary}
              onClick={presenter.terminateDevice}
              icon={<IconView Icon={IcStopPoll24Icon} />}
              title={t('trusted_device_delete_device')}
            />
            <MenuItem
              size="medium-2"
              style={MenuItemStyle.BrandPrimary}
              onClick={presenter.blockDevice}
              icon={<IconView Icon={IcBlock24Icon} />}
              title={t('trusted_device_block_device')}
            />
          </>
        )}
        {presenter.session.device.status === DeviceStatus.Blocked && (
          <MenuItem
            size="medium-2"
            style={MenuItemStyle.BrandPrimary}
            onClick={presenter.unblockDevice}
            icon={<IconView Icon={IcUnlockIcon} />}
            title={t('trusted_device_unblock_device')}
          />
        )}
        <div className={styles.footer}>
          {presenter.session.device.status === DeviceStatus.Trusted && (
            <>
              <IconView
                className={styles.shield}
                Icon={IcShield16Icon}
                size={IconViewSizes.Small}
              />
              <div className={styles.trustedText}>{t('trusted_device_status_trusted')}</div>
            </>
          )}
          {presenter.session.device.status === DeviceStatus.Unlocked && (
            <div className={styles.trustedText}>
              {t('trusted_device_unblock_finish_login_hint')}
            </div>
          )}
        </div>
      </Popup>
    );
  });
